import React, { useState, useEffect, useRef, useCallback } from 'react';
import { NavLink, Link, useNavigate, useLocation, Outlet, Navigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { logout } from '../store/authSlice';
import {
  LayoutDashboard, FileText, Building2, Receipt, Users,
  CalendarCheck, BedDouble, MessageSquare, CalendarPlus,
  LogOut,
  Menu, Shield, ScrollText, Settings, Bell, UserPlus, X
} from 'lucide-react';
import NotificationBell from '../components/NotificationBell';
import { DarkModeToggle } from '../components/DarkModeToggle';
import './AdminLayout.scss';

const NAV_ITEMS = [
  { label: 'Dashboard',       icon: LayoutDashboard, path: '/admin/dashboard', section: 'dashboard' },
  { label: 'Enquiries',       icon: FileText,        path: '/admin/enquiries', section: 'enquiries' },
  { label: 'Bookings',        icon: CalendarCheck,   path: '/admin/bookings', section: 'bookings' },
  { label: 'Stays',           icon: BedDouble,       path: '/admin/stays', section: 'stays' },
  { label: 'Extensions',      icon: CalendarPlus,    path: '/admin/extensions', section: 'extensions' },
  { label: 'Support Cases',   icon: MessageSquare,   path: '/admin/support', section: 'support' },
  { label: 'Guests',          icon: Users,           path: '/admin/guests', section: 'guests' },
  { label: 'Partners',        icon: Building2,       path: '/admin/partners', section: 'partners' },
  { label: 'Approvals',       icon: BedDouble,        path: '/admin/listings', section: 'listings' },
  // { label: 'Chats', icon: MessageSquare, path: '/admin/chats' }, // DISABLED
  { label: 'Invoices',        icon: Receipt,         path: '/admin/invoices', section: 'invoices' },
  { label: 'Settings',        icon: Settings,        path: '/admin/settings', section: 'settings' },
  { label: 'Administrators',  icon: UserPlus,        path: '/admin/invitations'     },
  { label: 'Notifications',   icon: Bell,            path: '/admin/notification-settings', section: 'notifications' },
];

export default function AdminLayout() {
  const [mobileOpen, setMobileOpen]     = useState(false);
  const { user, isAuthenticated, isSessionRestored } = useSelector(s => s.auth);
  const dispatch   = useDispatch();
  const navigate   = useNavigate();
  const location   = useLocation();
  const sidebarRef = useRef(null);
  // Chat polling removed — chat feature is disabled

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (!mobileOpen) return;
    const first = sidebarRef.current?.querySelector('a, button');
    first?.focus();
  }, [mobileOpen]);

  const handleLogout = () => {
    dispatch(logout());
    navigate('/admin/login');
  };

  const initials = user?.name
    ? user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : 'AD';

  if (!isSessionRestored) {
    return <div role="status" className="admin-layout__loading">Restoring your session…</div>;
  }
  if (!isAuthenticated || !['ADMIN', 'FINANCE'].includes(user?.role)) return <Navigate to="/admin/login" replace />;
  const isFinance = user.role === 'FINANCE';
  // Only the existing invoice route is available to finance. Do not use a broad
  // /admin or /invoices prefix that would silently admit future routes.
  if (isFinance && !/^\/admin\/invoices\/?$/.test(location.pathname)) return <Navigate to="/admin/invoices" replace />;
  const hasConfiguredAccess = user?.is_super_admin !== true && user?.admin_access && typeof user.admin_access === 'object';
  const navItems = isFinance
    ? NAV_ITEMS.filter(item => item.path === '/admin/invoices')
    : NAV_ITEMS.filter(item => item.path !== '/admin/invitations' || user?.is_super_admin === true)
      .filter(item => !hasConfiguredAccess || !item.section || user.admin_access[item.section]?.view === true);
  const currentItem = NAV_ITEMS.find(item => location.pathname === item.path || location.pathname.startsWith(`${item.path}/`));
  if (!isFinance && hasConfiguredAccess && currentItem?.section && user.admin_access[currentItem.section]?.view !== true) {
    const fallback = navItems[0]?.path;
    return fallback ? <Navigate to={fallback} replace /> : <div role="alert" className="admin-layout__loading">Your administrator account has no portal access.</div>;
  }

  return (
    <div className="admin-layout">

      {mobileOpen && (
        <div
          className="admin-overlay"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      <aside
        ref={sidebarRef}
        className={`admin-sidebar ${mobileOpen ? 'admin-sidebar--open' : ''}`}
        aria-label="Admin navigation"
      >
        {/* Logo */}
        <div className="admin-sidebar__logo">
          <Shield size={22} className="admin-sidebar__logo-icon" aria-hidden="true" />
          <div className="admin-sidebar__brand">
            <span className="admin-sidebar__logo-text">
              <span className="admin-sidebar__logo-text-primary">yet2</span><span className="admin-sidebar__logo-text-accent">book</span>
            </span>
            <span className="admin-sidebar__brand-subtitle">Admin Portal</span>
          </div>
        </div>

        {/* Nav */}
        <nav className="admin-sidebar__nav" aria-label="Main admin menu">
          <ul role="list">
            {navItems.map(({ label, icon: Icon, path }) => {
              const badge = 0; // Chat badge disabled
              return (
                <li key={path}>
                  <NavLink
                    to={path}
                    className={({ isActive }) =>
                      `admin-sidebar__link ${isActive ? 'admin-sidebar__link--active' : ''}`
                    }
                    aria-label={label}
                    end={path === '/admin/dashboard'}
                    style={{ position: 'relative' }}
                  >
                    <span style={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
                      <Icon size={18} className="admin-sidebar__link-icon" aria-hidden="true" />
                      {badge > 0 && (
                        <span style={{ position: 'absolute', top: -3, right: -4, width: 8, height: 8, background: '#ef4444', borderRadius: '50%', border: '1.5px solid #1e293b' }} />
                      )}
                    </span>
                    <span className="admin-sidebar__link-label">{label}</span>
                    {badge > 0 && (
                      <span style={{ background: '#ef4444', color: '#fff', borderRadius: 10, fontSize: 10, fontWeight: 700, padding: '1px 6px', marginLeft: 'auto', flexShrink: 0 }}>
                        {badge}
                      </span>
                    )}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* User footer */}
        <div className="admin-sidebar__footer">
          {isFinance ? (
            <div className="admin-sidebar__user">
              <div className="admin-sidebar__avatar" aria-hidden="true">{initials}</div>
              <div className="admin-sidebar__user-info">
                <span className="admin-sidebar__user-name">{user.name}</span>
                <span className="admin-sidebar__user-role">Finance</span>
              </div>
            </div>
          ) : (
          <div className="admin-sidebar__user">
            <div className="admin-sidebar__avatar" aria-hidden="true">{initials}</div>
            <div className="admin-sidebar__user-info">
              <span className="admin-sidebar__user-name">{user?.name || 'Admin'}</span>
              <span className="admin-sidebar__user-role">{user?.is_super_admin ? 'Super Admin' : 'Administrator'}</span>
            </div>
          </div>)}
          <button
            className="admin-sidebar__logout"
            onClick={handleLogout}
            aria-label="Sign out"
            title="Sign out"
          >
            <LogOut size={16} aria-hidden="true" />
            <span>Sign out</span>
          </button>
        </div>
      </aside>

      {/* Main area */}
      <div className="admin-main">
        <header className="admin-topbar" role="banner">
          <button
            className="admin-topbar__menu-btn"
            onClick={() => setMobileOpen(o => !o)}
            aria-label="Open navigation menu"
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>

          <div className="admin-topbar__page-title">
            {navItems.find(n => location.pathname.startsWith(n.path))?.label || 'Admin'}
          </div>

          <div className="admin-topbar__actions">
            <DarkModeToggle />
            {!isFinance && <NotificationBell />}

          </div>
        </header>

        <main className="admin-content" id="main-content" tabIndex={-1}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
